import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { SessionsManagementCard } from '@/components/profile/sessions-management-card';
import {
    SessionUserProfileDetailedResponse,
    SetupTotpResponse,
    SessionUserProfileDetailedResponseSchema,
    SetupTotpResponseSchema,
    GenerateBackupCodesResponseSchema,
} from '@pillar-portal/api-types';

function SecurityContent() {
    const log = createComponentLogger('ProfileSecurityPage');
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [totpCode, setTotpCode] = useState('');
    const [showTotpSetup, setShowTotpSetup] = useState(false);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [showDisableTotpConfirm, setShowDisableTotpConfirm] = useState(false);

    const { data: profile, isLoading: profileLoading } =
        useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await authApi.getProfile();
            if (response === null || response === undefined) {
                return null;
            }
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        enabled: !!user, // Only fetch if user is logged in
    });

    const { data: totpSetup } = useQuery<SetupTotpResponse>({
        queryKey: ['totp-setup'],
        queryFn: async () => {
            const response = await authApi.setupTOTP();
            return SetupTotpResponseSchema.parse(response);
        },
        enabled: showTotpSetup && !profile?.totp_enabled,
    });

    const enableTotpMutation = useMutation({
        mutationFn: authApi.enableTOTP,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
            setShowTotpSetup(false);
            setTotpCode('');
        },
    });

    const disableTotpMutation = useMutation({
        mutationFn: authApi.disableTOTP,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
        },
    });

    const generateBackupCodesMutation = useMutation({
        mutationFn: async () => {
            const response = await authApi.generateBackupCodes();
            return GenerateBackupCodesResponseSchema.parse(response);
        },
        onSuccess: (data) => {
            setBackupCodes(data.codes);
        },
    });

    const handleEnableTOTP = (e: React.FormEvent) => {
        e.preventDefault();
        if (totpCode && totpSetup) {
            enableTotpMutation.mutate({
                secret: totpSetup.secret,
                code: totpCode,
            });
        }
    };

    const handleCopyBackupCodes = () => {
        runInBackground(navigator.clipboard.writeText(backupCodes.join('\n')), (err) => {
            log.error('Error copying backup codes to clipboard', err);
        });
    };

    if (profileLoading) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">Security Settings</h1>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">Security Settings</h1>
            </div>

            <div className="max-w-2xl space-y-6">
                {/* Two-Factor Authentication */}
                <Card>
                    <CardHeader>
                        <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
                        <CardDescription>
                            Add an extra layer of security to your account
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                                <p className="font-medium">TOTP Authentication</p>
                                <p className="text-sm text-muted-foreground">
                                    Use an authenticator app to generate codes
                                </p>
                            </div>
                            <Badge variant={profile?.totp_enabled ? 'default' : 'secondary'}>
                                {profile?.totp_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {!profile?.totp_enabled && !showTotpSetup && (
                            <Button onClick={() => setShowTotpSetup(true)}>Enable 2FA</Button>
                        )}

                        {!profile?.totp_enabled && showTotpSetup && totpSetup && (
                            <div className="space-y-4">
                                <Alert>
                                    <AlertDescription>
                                        Scan this QR code with your authenticator app (Google
                                        Authenticator, Authy, etc.)
                                    </AlertDescription>
                                </Alert>

                                <div className="flex justify-center p-4 bg-white border rounded-lg">
                                    <img src={totpSetup.qr_code} alt="TOTP QR Code" />
                                </div>

                                <div className="space-y-2">
                                    <Label>Or enter this secret manually:</Label>
                                    <Input
                                        value={totpSetup.secret}
                                        readOnly
                                        className="font-mono text-sm"
                                    />
                                </div>

                                <form onSubmit={handleEnableTOTP} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="totpCode">Verification Code</Label>
                                        <Input
                                            id="totpCode"
                                            type="text"
                                            value={totpCode}
                                            onChange={(e) => setTotpCode(e.target.value)}
                                            placeholder="Enter 6-digit code"
                                            maxLength={6}
                                            required
                                            disabled={enableTotpMutation.isPending}
                                        />
                                    </div>

                                    {enableTotpMutation.error && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {enableTotpMutation.error instanceof Error
                                                    ? enableTotpMutation.error.message
                                                    : 'Failed to enable 2FA'}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="flex gap-2">
                                        <Button
                                            type="submit"
                                            disabled={
                                                enableTotpMutation.isPending ||
                                                totpCode.length !== 6
                                            }
                                        >
                                            {enableTotpMutation.isPending
                                                ? 'Verifying...'
                                                : 'Enable 2FA'}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                                setShowTotpSetup(false);
                                                setTotpCode('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {profile?.totp_enabled && (
                            <div className="space-y-4">
                                <Alert className="bg-green-50 border-green-200">
                                    <AlertDescription className="text-green-800">
                                        Two-factor authentication is enabled on your account
                                    </AlertDescription>
                                </Alert>
                                <Button
                                    variant="destructive"
                                    onClick={() => setShowDisableTotpConfirm(true)}
                                    disabled={disableTotpMutation.isPending}
                                >
                                    Disable 2FA
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Backup Codes */}
                {profile?.totp_enabled && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Backup Codes</CardTitle>
                            <CardDescription>
                                Use backup codes to access your account if you lose your
                                authenticator
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {backupCodes.length === 0 ? (
                                <Button
                                    onClick={() => generateBackupCodesMutation.mutate()}
                                    disabled={generateBackupCodesMutation.isPending}
                                >
                                    {generateBackupCodesMutation.isPending
                                        ? 'Generating...'
                                        : 'Generate Backup Codes'}
                                </Button>
                            ) : (
                                <div className="space-y-4">
                                    <Alert>
                                        <AlertDescription>
                                            Save these codes in a safe place. Each code can only be
                                            used once.
                                        </AlertDescription>
                                    </Alert>
                                    <div className="p-4 bg-muted rounded-lg space-y-2">
                                        {backupCodes.map((code, idx) => (
                                            <div key={idx} className="font-mono text-sm">
                                                {code}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={handleCopyBackupCodes}>Copy Codes</Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setBackupCodes([])}
                                        >
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                <SessionsManagementCard
                    mode="user"
                    showSearch={false}
                    showHeader={false}
                    title="Active Sessions"
                    description="Manage your active login sessions across different devices"
                />
            </div>

            {/* Disable 2FA Confirmation Modal */}
            <ConfirmationModal
                open={showDisableTotpConfirm}
                onOpenChange={setShowDisableTotpConfirm}
                message="Are you sure you want to disable 2FA? This will make your account less secure."
                onConfirm={() => {
                    disableTotpMutation.mutate();
                    setShowDisableTotpConfirm(false);
                }}
                confirmLabel="Disable 2FA"
                isLoading={disableTotpMutation.isPending}
            />

        </div>
    );
}

export default function SecurityPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <SecurityContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
